import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject
} from '@angular/core';
import { GenericLeg, LegAttribute, QuotePair } from '@approot/models';
import { AggregatedValues, LegsToAggregate } from '@approot/models/summed-legs';
import {
  ConfigService,
  EntityMapperService,
  StrategyDetailService
} from '@approot/services';
import { LegService } from '@approot/services/leg/leg.service';
import { clone } from '@approot/utilities';
import { isEqual } from 'lodash';
import { Debounce } from 'lodash-decorators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sum-leg',
  templateUrl: './sum-leg.component.html',
  styleUrls: ['./sum-leg.component.scss']
})
export class SumLegComponent implements OnDestroy, OnChanges {
  @Input() public tabsLegsToAggregate: LegsToAggregate;
  @Input() public aggregatedValues: AggregatedValues;
  @Input() public entity: string;
  @Input() public tradeViewMode: boolean;
  public cross: QuotePair;
  public loading = false;
  public activeAttributes: LegAttribute[];
  public entities: string[] = [];
  private aggregateResultsSub: Subscription;
  private strategyDetailService = inject(StrategyDetailService);
  private entityMapperService = inject(EntityMapperService);

  constructor(
    private legService: LegService,
    private configService: ConfigService
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    const tabsLegsToAggregateChanges = changes.tabsLegsToAggregate;
    if (tabsLegsToAggregateChanges) {
      if (tabsLegsToAggregateChanges.isFirstChange()) {
        this.setSumSetupData();
      } else if (
        tabsLegsToAggregateChanges.currentValue &&
        !isEqual(
          tabsLegsToAggregateChanges.currentValue,
          tabsLegsToAggregateChanges.previousValue
        )
      ) {
        this.handleChangeInLegsToAggregate();
      }
    }

    if (
      changes.tradeViewMode?.currentValue != null &&
      !changes.tradeViewMode.isFirstChange()
    ) {
      this.tradeViewModeChange();
    }
  }

  private tradeViewModeChange(): void {
    this.activeAttributes = this.legService.activeAttributes();
    this.strategyDetailService.tabsState.update(state => {
      const clonedState = clone(state);
      const tab = clonedState.tabs[clonedState.selectedTab];
      tab.aggregatedValues = null;
      return clonedState;
    });

    if (!this.tradeViewMode) {
      this.entities = [];
      this.updateEntity(null);
      this.aggregateResults(null);
    }
  }

  public ngOnDestroy(): void {
    this.aggregateResultsSub?.unsubscribe();
  }

  public entityChange(entity: string): void {
    this.updateEntity(entity);
    this.aggregateResults(entity);
  }

  public setEntities(legs: GenericLeg[]): void {
    const flatCounterParties =
      legs?.map(leg => [leg.counterPartyA, leg.counterPartyB]).flat() || [];

    this.entities = [...new Set(flatCounterParties)].filter(
      counterParty => counterParty
    );

    if (!this.entities.length) {
      this.updateEntity(null);
      return;
    }

    if (!this.entities.includes(this.entity)) {
      this.updateEntity(this.entities[0]);
      return;
    }
  }

  private updateEntity(entity: string): void {
    this.strategyDetailService.tabsState.update(state => {
      const clonedState = clone(state);
      const tab = clonedState.tabs[clonedState.selectedTab];
      tab.aggregateLegsEntity = entity;
      return clonedState;
    });
  }

  @Debounce(100)
  private handleChangeInLegsToAggregate(): void {
    if (!this.tabsLegsToAggregate) {
      return;
    }
    this.strategyDetailService.tabsState.update(state => {
      const clonedState = clone(state);
      clonedState.tabs[clonedState.selectedTab].aggregatedValues = null;
      return clonedState;
    });
    this.aggregateResultsSub?.unsubscribe();
    this.aggregateResults(this.entity);
  }

  private setSumSetupData(): void {
    if (!this.isLegsToAggregate()) {
      return;
    }
    this.activeAttributes = this.legService.activeAttributes();
    this.cross = this.currencyPairToUse(this.tabsLegsToAggregate);
  }

  private isLegsToAggregate(): boolean {
    return (
      this.tabsLegsToAggregate?.legs?.length &&
      this.tabsLegsToAggregate.legs.every(leg => leg.result)
    );
  }

  private aggregateResults(entity: string): void {
    if (!this.isLegsToAggregate()) {
      return;
    }
    this.setSumSetupData();
    this.strategyDetailService.tabsState.update(state => {
      const clonedState = clone(state);
      clonedState.tabs[clonedState.selectedTab].aggregatedValues = null;
      return clonedState;
    });
    this.loading = true;
    this.aggregateResultsSub?.unsubscribe();
    this.aggregateResultsSub = this.legService
      .aggregateResults({
        results: this.tabsLegsToAggregate.legs.map(y => y.result),
        midFlags: this.tabsLegsToAggregate.legs.map(y => y.isMidSelected),
        net_vanilla_spreads:
          !!this.configService.userSettings().parent[
            'pricing:netVanillaSpreads'
          ],
        entity: entity
          ? this.entityMapperService.getRealEntity(entity)
          : undefined
      })
      .subscribe({
        next: response => {
          const value = response.pricing;
          const premiumQuoteCurrency =
            value.price?.premiumBaseCurrency ||
            value.price?.premiumQuoteCurrency
              ? value.price?.premiumQuoteCurrency
              : value.price?.premiumReportingCurrency;

          this.strategyDetailService.tabsState.update(state => {
            const clonedState = clone(state);
            const tab = clonedState.tabs[clonedState.selectedTab];
            tab.aggregatedValues = {
              headers: value.headers,
              premiumQuoteCurrency,
              premiumBaseCurrency: value.price?.premiumBaseCurrency,
              delta: value.spotDeltas?.baseCurrency,
              gamma: value.gammas?.baseCurrency,
              vega: value.vegas?.baseCurrency,
              flies: value.flies?.quoteCurrency,
              riskies: value.riskies?.quoteCurrency,
              blackScholesMidTv: value.price.pvQuoteCurrency?.bsMid,
              premiumQuoteCurrencyInPercent:
                value.price.premiumQuoteCurrencyInPercent,
              premiumBaseCurrencyInPercent:
                value.price.premiumBaseCurrencyInPercent,
              premiumQuoteCurrencyPips: value.price.premiumQuoteCurrencyPips
            };
            return clonedState;
          });
          this.loading = false;
        },
        error: _ => (this.loading = false)
      });
  }

  private currencyPairToUse(data: LegsToAggregate): QuotePair {
    // get the currency that is to be used in the sum pricing leg
    const leg1price = data?.legs[0]?.result?.pricing?.price;
    if (!leg1price) {
      return null;
    }

    if (data.useReportingCurrency) {
      return { base: '', term: leg1price.reportingCurrency };
    }

    return {
      base: leg1price.baseCurrency,
      term: leg1price.quoteCurrency
    };
  }
}
